import { useEffect, useMemo, useRef, useState } from "react"
// Imported from the submodules instead of the package root because
// react-use-websocket is CJS-only and exposes the hook via `exports.default`,
// which Vite 8's Node-consistent CJS interop no longer unwraps.
import { ReadyState } from "react-use-websocket/dist/lib/constants"
import { useWebSocket } from "react-use-websocket/dist/lib/use-websocket"

import { RepeatUntilHandle } from "shared/lib/repeatUntil"
import { useEvent } from "shared/lib/useEvent"
import { useTimeout } from "shared/lib/useTimeout"
import {
  ExternalDimensioningResult,
  ProductConfigurationModelDimInfo
} from "shared/types/__generated__/graphql"

import {
  useExternalDimensioningModelsQuery,
  useExternalDimensioningResultQuery
} from "entities/dim-configuration/graphql/external-dimensioning.queries.generated"
import { useExternalDimensioningDataExchange } from "entities/products/model/useExternalDimensioningDataExchange"

export enum ExternalDimensioningOrigin {
  PORTAL = "portal",
  APROGRAMM = "aprogramm"
}

export interface UseExternalProductDimensioningModelArgs {
  productType: string
  defaultExternalDimensioningResult?: ExternalDimensioningResult | null
  onExternalDimensioningChange?: (
    externalDimensioningResult: ExternalDimensioningResult
  ) => void
}

export function useExternalProductDimensioningModel({
  productType,
  defaultExternalDimensioningResult,
  onExternalDimensioningChange
}: UseExternalProductDimensioningModelArgs) {
  const [exchangeID, setExchangeID] = useState<string | null>(null)
  const [
    previousExternalDimensioningResult,
    setPreviousExternalDimensioningResult
  ] = useState(defaultExternalDimensioningResult)
  const [isPolling, setIsPolling] = useState(false)

  const pendingSetupRef =
    useRef<RepeatUntilHandle<ExternalDimensioningResult> | null>(null)
  const pollingTimeout = useTimeout()

  const wsQueryParams = useMemo(
    () => ({ transactionId: exchangeID ?? "" }),
    [exchangeID]
  )

  const handleMessage = useEvent((event: MessageEvent) => {
    const parsedMessage = JSON.parse(event.data)
      .dimensioning as ExternalDimensioningResult

    if (
      parsedMessage.transferData?.origin ===
      ExternalDimensioningOrigin.APROGRAMM
    ) {
      setPreviousExternalDimensioningResult(parsedMessage)
      onExternalDimensioningChange?.(parsedMessage)
    }
  })

  const { readyState } = useWebSocket(
    `${import.meta.env.VITE_DIM_EXCHANGE_WEBSOCKET}`,
    { queryParams: wsQueryParams, onMessage: handleMessage },
    !!exchangeID
  )

  const {
    data: pollData,
    startPolling,
    stopPolling
  } = useExternalDimensioningResultQuery({
    variables: { id: exchangeID ?? "" },
    skip: !exchangeID || !isPolling,
    fetchPolicy: "network-only"
  })

  const { post } = useExternalDimensioningDataExchange()

  const setup: (
    externalDimensioningResult?: ExternalDimensioningResult | null | undefined
  ) => Promise<ExternalDimensioningResult> = useEvent(
    externalDimensioningResult => {
      pollingTimeout.clear()
      stopPolling()
      setIsPolling(false)
      pendingSetupRef.current?.cancel()
      const handle = post(externalDimensioningResult, { freshID: true })
      pendingSetupRef.current = handle
      return handle.promise.then(result => {
        setPreviousExternalDimensioningResult(result)
        setExchangeID(result.transactionId)
        return result
      })
    }
  )

  const { data: modelData, loading: isLoading } =
    useExternalDimensioningModelsQuery()

  // Start the 10s fallback timer once the exchange ID is available
  useEffect(() => {
    if (exchangeID && readyState !== ReadyState.OPEN) {
      pollingTimeout.start(10_000, () => setIsPolling(true))
    }

    return pollingTimeout.clear
  }, [exchangeID])

  // Start Apollo polling when isPolling flips true
  useEffect(() => {
    if (isPolling) startPolling(2_000)
  }, [isPolling])

  // Cancel polling if the WS connection comes up late
  useEffect(() => {
    if (readyState === ReadyState.OPEN && isPolling) {
      pollingTimeout.clear()
      stopPolling()
      setIsPolling(false)
    }
  }, [readyState])

  // Handle poll results arriving
  useEffect(() => {
    if (!isPolling) return
    const result = pollData?.externalDimensioningResult
    if (result?.transferData?.origin === ExternalDimensioningOrigin.APROGRAMM) {
      stopPolling()
      setIsPolling(false)
      setPreviousExternalDimensioningResult(result)
      onExternalDimensioningChange?.(result)
    }
  }, [pollData, isPolling])

  useEffect(() => {
    if (!defaultExternalDimensioningResult) {
      setup()
    }
    return () => {
      pendingSetupRef.current?.cancel()
    }
  }, [])

  const reset = useEvent(() => {
    setup(previousExternalDimensioningResult)
  })

  const baseURLs = useMemo<ProductConfigurationModelDimInfo[] | null>(() => {
    return (
      modelData?.externalDimensioningModels?.models?.find(
        model => model.id === productType
      )?.dimUrl || null
    )
  }, [modelData])

  const externalProductDimensioningURLs = useMemo<
    ProductConfigurationModelDimInfo[]
  >(() => {
    if (!baseURLs || baseURLs.length === 0 || !exchangeID) {
      return []
    }

    return baseURLs.map(base => ({
      url: base.url.replace("__CODE_JR__", exchangeID),
      label: base.label,
      __typename: "ProductConfigurationModelDimInfo"
    }))
  }, [productType, exchangeID, baseURLs])

  const resolvedExternalDimensioningResult =
    useMemo<ExternalDimensioningResult | null>(() => {
      if (
        previousExternalDimensioningResult?.transferData?.origin ===
        ExternalDimensioningOrigin.PORTAL
      ) {
        return null
      }
      if (previousExternalDimensioningResult?.variantKeyData) {
        return previousExternalDimensioningResult
      }
      return defaultExternalDimensioningResult || null
    }, [previousExternalDimensioningResult, defaultExternalDimensioningResult])

  return {
    externalDimensioningResult: resolvedExternalDimensioningResult,
    externalProductDimensioningURLs,
    isExternalProductDimensioningAvailable: (baseURLs?.length ?? 0) > 0,
    isLoadingExternalDimensioningModels: isLoading,
    isLoading: readyState !== ReadyState.OPEN,
    reset
  }
}
